import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UiKitModule } from '@serviceplan/ui-kit';
import { ClientEnvironment, ENVIRONMENT, TagsDataConfig } from '@thehubz/client/core';
import { TagsWithAssetCount } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Observable } from 'rxjs';
import { TagsChipComponent } from './tags-chip/tags-chip.component';

@Component({
  selector: 'thehubz-tags-dialog',
  templateUrl: './tags-dialog.component.html',
  styleUrls: ['./tags-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule, MatDialogModule, MatCheckboxModule, MatPaginatorModule, MatTooltipModule, UiKitModule, TagsChipComponent],
})
export class TagsDialogComponent {
  /** Store tag list from api */
  tagsListWithAssetCount!: TagsWithAssetCount[];
  /** Store filtered tag list from the search input */
  filteredTagsList!: TagsWithAssetCount[];
  /** Store selected tag list */
  selectedTagsList: TagsWithAssetCount[] = [];
  /** Connect with the MatTableDataSource */
  tagsObs!: Observable<TagsWithAssetCount[]>;
  /** Store filter search text */
  searchText = '';
  /** Search placeholder */
  searchPlaceholder = $localize`Search tags...`;
  /** Show tag empty state */
  showEmptyState = false;

  selectDefaultKeywordFlag = false;
  /** Paginator object */
  paginatorObj: Partial<PageEvent> & Pick<PageEvent, 'pageIndex' | 'pageSize'> = {
    pageIndex: 0,
    pageSize: 20,
  };
  /** Disable apply tag button */
  disableApply = true;

  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;

  readonly brandingKey: CustomClients;
  /** Mat-paginator properties */
  /** Number of items to display on a page */
  dataSource!: MatTableDataSource<TagsWithAssetCount>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private dialogRef: MatDialogRef<TagsDialogComponent, TagsWithAssetCount[]>,
    @Inject(MAT_DIALOG_DATA) public data: TagsDataConfig,
  ) {
    this.brandingKey = this.env.brandingKey || CustomClients.Standard;
  }

  /**
   * Assign paginator to MatTableDataSource paginator
   */
  paginatorAssignment(event: { selectedTagsList: TagsWithAssetCount[]; disableApply?: boolean; currentlySelectedTagList?: TagsWithAssetCount[] }) {
    this.tagsListWithAssetCount = event.selectedTagsList;
    if (event.currentlySelectedTagList) {
      this.selectedTagsList = event.currentlySelectedTagList;
    }
    let sortedTags = this.tagsListWithAssetCount;
    if (this.brandingKey !== CustomClients.SEAT) {
      sortedTags = (this.tagsListWithAssetCount?.length && this.tagsListWithAssetCount.sort((a, b) => a.name.localeCompare(b.name))) || [];
    }
    setTimeout(() => {
      this.dataSource = new MatTableDataSource<TagsWithAssetCount>(sortedTags);
      this.dataSource.paginator = this.paginator;
      this.tagsObs = this.dataSource.connect();
      this.disableApply = event.disableApply ?? true;
    });
  }

  searchedTags(event: { selectedTagsList: TagsWithAssetCount[]; disableApply?: boolean; selectedTagName?: string }) {
    this.selectedTagsList = event.selectedTagsList;
    this.disableApply = event.disableApply ?? true;
  }

  /**
   * Filter tags based on the search filter
   * @param event
   */
  searchTextChanged(event: AnyType) {
    this.searchText = event;
  }
  /**
   * Clear search filter
   */
  clearSearch() {
    this.searchText = '';
    this.filteredTagsList = this.tagsListWithAssetCount;
    this.paginatorAssignment({ selectedTagsList: this.filteredTagsList });
  }

  selectDefaultKeywords(evt: AnyType) {
    this.selectDefaultKeywordFlag = evt.checked;
  }

  /**
   * Close dialog and send the searchTagsList data to parent component
   */
  applyTags() {
    this.dialogRef.close(this.selectedTagsList);
  }

  /**
   * Pagination change event
   * @param event
   */
  onPaginateChange(event: PageEvent) {
    this.paginatorObj = event;
  }
}
